import { useRef } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  allDdRequestsKey,
  fetchAllDdRequests,
  otherItemsWaitingOn,
  patchDdRequestInCache,
  answerQuestion,
  api,
  ApiError,
  contractsQueryKey,
  correctContract,
  fetchContractRegister,
  uploadContractCurrentCopy,
  documentSearchQueryKey,
  fetchDocumentSearch,
  hasSearchCriteria,
  listQuestions,
  documentsPollTracker,
  fetchProcessingView,
  estimateQueryKey,
  estimateRefetchInterval,
  fetchProcessingEstimate,
  type ProcessingEstimate,
  nextDocumentsPoll,
  processingPollDocs,
  processingQueryKey,
  uploadRangeDays,
  type ProcessingParams,
  type ProcessingViewResponse,
  questionsQueryKey,
  buyerQuestionsQueryKey,
  listBuyerQuestions,
  fetchVersionSuggestions,
  resolveVersionSuggestions,
  versionSuggestionsQueryKey,
  type BulkSupport,
  type VersionSuggestionAction,
  documentVersionsQueryKey,
  fetchDocumentVersions,
  versionsRefetchInterval,
  PendingCategoryError,
  type AddCategoryAndFileInput,
  type AddCategoryAndFileResult,
  type CategoryImportResponse,
} from '@dataroom/shared';
import { sellerRooms } from '@/config';
import type {
  CorrectContractRequest,
  AnswerDdPhaseQuestionRequest,
  AnswerQuestionRequest,
  CertifySectionRequest,
  CreateDdAccountRequest,
  CreateDdAccountResponse,
  DdAccount,
  ListDdAccountsResponse,
  ListDdStatesResponse,
  SetDdStatesResponse,
  UpdateDdAccountRequest,
  DdPhaseQuestion,
  DdPhaseSummary,
  DdRequest,
  DdRequestsSummaryResponse,
  IngestFinalizeRequest,
  IngestFinalizeResponse,
  ListCategoriesResponse,
  ListCertificationsResponse,
  ListDdPhaseQuestionsResponse,
  ListDdRequestsQuery,
  ListDdRequestsResponse,
  ListDocumentsResponse,
  SetStatementBasisRequest,
  SetStatementBasisResponse,
  Exclusivity,
  ExclusivityValidationRequest,
  ExclusivityValidationResponse,
  ExclusivityView,
  ExtendExclusivityRequest,
  ListLoiOutcomesResponse,
  ListLoisResponse,
  ListMilestonesResponse,
  ModifyDocumentAttributesRequest,
  ModifyDocumentAttributesResponse,
  ReclassifyDocumentResponse,
  SetDocumentDatingRequest,
  SetDocumentDatingResponse,
  ListRisksResponse,
  LoiDecisionRequest,
  LoiDecisionResponse,
  LoiSellerVoteRequest,
  Room,
  RoomDocument,
  SearchDocumentsQuery,
  SectionCertification,
  SessionResponse,
  WithdrawCertificationResponse,
} from '@dataroom/shared';

/** The signed-in actor's identity + the rooms they are a principal of
 * (GET /auth/session). The submit portal resolves its room from this instead of
 * a hardcoded stub — see {@link sellerRoomId}. `retry: false` so a 401 surfaces
 * immediately (the page redirects to /login on it). */
export function useSession() {
  return useQuery({
    queryKey: ['auth', 'session'],
    queryFn: () => api.get<SessionResponse>('/auth/session'),
    retry: false,
  });
}

export type SellerRoomAccess =
  | { status: 'login' }
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'ok'; roomId: string };

/**
 * Resolves a room-scoped seller view from the `:roomId` path param, verifying it
 * is actually one of the caller's SELLER rooms (never trust the id in the URL).
 * Every room-scoped page funnels through this so the guard lives in one place:
 * 'login' → /login, 'loading' → spinner, 'forbidden' → back to the landing.
 */
export function useSellerRoomAccess(): SellerRoomAccess {
  const session = useSession();
  const { roomId } = useParams<{ roomId: string }>();
  if (session.error instanceof ApiError && session.error.status === 401) return { status: 'login' };
  if (session.isPending) return { status: 'loading' };
  const rooms = sellerRooms(session.data);
  if (!roomId || !rooms.some((r) => r.roomId === roomId)) return { status: 'forbidden' };
  return { status: 'ok', roomId };
}

/** What the shared poll cadence {@link nextDocumentsPoll} remembers (in-flight
 * documents and when they last moved). Module-level: shared by every mounted
 * documents query, which all read the same cache entry. */
const documentsPoll = documentsPollTracker();

/** The room's library as the bound seller sees it — every document, filed or
 * not, whoever uploaded it, minus deal paperwork (server-side, ADR-0028). Polls while ANY document is still `processing`, on the SAME
 * cadence as the admin app (`nextDocumentsPoll`: fast while documents keep
 * settling, backing off once nothing moves, giving up after 10 minutes without
 * progress, stopping on an error) and stops once none are, so
 * a just-uploaded document advances from Received to Filed with no reload. When
 * a poll sees a document LEAVE `processing`, its filing may have changed the
 * folder tree, the checklist (requested-item matches) and any search, so those
 * are refreshed too.
 * `retry: false` so a 401 surfaces immediately (SubmitPage redirects to /login).
 * `enabled: false` reads nothing (the Processing page loads the listing only when
 * it must find a document by id). */
export function useRoomDocuments(roomId: string, options: { enabled?: boolean } = {}) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['rooms', roomId, 'documents'],
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const before = qc.getQueryData<RoomDocument[]>(['rooms', roomId, 'documents']);
      const docs = await api
        .get<ListDocumentsResponse>(`/rooms/${roomId}/documents`)
        .then((r) => r.documents);
      if (before) {
        const nowById = new Map(docs.map((d) => [d.id, d.classificationStatus] as const));
        const settled = before.some(
          (d) => d.classificationStatus === 'processing' && nowById.get(d.id) !== 'processing',
        );
        if (settled) {
          void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
          // Filing may also have matched the document to requested items.
          void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] });
          void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-summary'] });
          void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'search'] });
          // …and may have found a possible new version of another document.
          void qc.invalidateQueries({ queryKey: versionSuggestionsQueryKey(roomId) });
        }
      }
      return docs;
    },
    retry: false,
    refetchInterval: (query) =>
      nextDocumentsPoll(
        query.state.data,
        documentsPoll,
        Date.now(),
        query.state.status === 'error',
      ),
  });
}

/** The seller corrects a financial statement's accounting basis (ADR-0053) — audited server-side. */
export function useSetStatementBasis(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: SetStatementBasisRequest }) =>
      api.put<SetStatementBasisResponse>(
        `/rooms/${roomId}/documents/${documentId}/statement-basis`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] }),
  });
}

/** What a filing change can move: the listing, the folder tree (counts), the
 * checklist's matches, any search, and a possible new version of another document. */
function invalidateAfterFiling(qc: ReturnType<typeof useQueryClient>, roomId: string): void {
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-summary'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'search'] });
  void qc.invalidateQueries({ queryKey: versionSuggestionsQueryKey(roomId) });
}

/** The seller side moves a document to another folder (owner decision
 * 2026-10-21) — the category PATCH, recorded as the person's decision and
 * audited under them, exactly as for the room admin. */
export function useModifyDocumentAttributes(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      body,
    }: {
      documentId: string;
      body: ModifyDocumentAttributesRequest;
    }) =>
      api.patch<ModifyDocumentAttributesResponse>(`/rooms/${roomId}/documents/${documentId}`, body),
    onSuccess: () => invalidateAfterFiling(qc, roomId),
  });
}

/** The seller side sets or clears a document's period and/or date of data
 * (only the keys sent change; null clears) — audited under them. */
export function useSetDocumentDating(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: SetDocumentDatingRequest }) =>
      api.put<SetDocumentDatingResponse>(`/rooms/${roomId}/documents/${documentId}/dating`, body),
    onSuccess: () => invalidateAfterFiling(qc, roomId),
  });
}

/** "Re-file with AI": run the classifier again on one document (a person's own
 * filing is never touched by it). */
export function useReclassifyDocument(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.post<ReclassifyDocumentResponse>(`/rooms/${roomId}/documents/${documentId}/reclassify`),
    onSuccess: () => invalidateAfterFiling(qc, roomId),
  });
}

/**
 * "Add a new subfolder…" — ONE action: create the subfolder inside an existing
 * folder through the folder import (`parentTagId`: it shares its parent's
 * audience, and the server allows the seller side only this one-subfolder form)
 * and then file the document there through the category PATCH.
 */
export function useAddCategoryAndFile(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddCategoryAndFileInput): Promise<AddCategoryAndFileResult> => {
      let categoryId = input.existingId;
      let created = false;
      if (categoryId === null) {
        const res = await api.post<CategoryImportResponse>(`/rooms/${roomId}/tags/import`, {
          nodes: [{ name: input.name }],
          parentTagId: input.parentId,
        });
        const node = res.nodes[0];
        if (!node) throw new Error('The subfolder could not be created.');
        if (!node.created && !input.knownIds.has(node.id)) throw new PendingCategoryError();
        categoryId = node.id;
        created = node.created;
      }
      await api.patch<ModifyDocumentAttributesResponse>(
        `/rooms/${roomId}/documents/${input.documentId}`,
        { categoryTagIds: input.categoryIdsFor(categoryId) },
      );
      return { categoryId, created };
    },
    onSettled: () => invalidateAfterFiling(qc, roomId),
  });
}

/** The room's open "Possible new version of …" suggestions (ADR-0031). */
export function useVersionSuggestions(roomId: string) {
  return useQuery({
    queryKey: versionSuggestionsQueryKey(roomId),
    queryFn: () => fetchVersionSuggestions(roomId),
    retry: false,
  });
}

/** "Merge as new version" / "Keep separate" on one or several suggestions (in
 * bulk when the server can, else one by one — resolveVersionSuggestions). Never
 * throws: each suggestion's outcome comes back. The server only lets the seller
 * decide suggestions it marks `canDecide`. The listing, the folder views and the
 * suggestions all re-read afterwards. */
export function useResolveVersionSuggestions(roomId: string) {
  const qc = useQueryClient();
  const bulk = useRef<BulkSupport>({ supported: null });
  return useMutation({
    mutationFn: ({ ids, action }: { ids: readonly string[]; action: VersionSuggestionAction }) =>
      resolveVersionSuggestions(roomId, ids, action, bulk.current),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'search'] });
      void qc.invalidateQueries({ queryKey: versionSuggestionsQueryKey(roomId) });
      void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
    },
  });
}

/** A document's version history (ADR-0031): the room admin, co-admins and the bound seller. */
export function useDocumentVersions(roomId: string, documentId: string) {
  return useQuery({
    queryKey: documentVersionsQueryKey(roomId, documentId),
    queryFn: () => fetchDocumentVersions(roomId, documentId),
    retry: false,
    refetchInterval: (query) => versionsRefetchInterval(query.state.data),
  });
}

/** PRD-0004 §4 "find a document": free-text search across the seller's own
 * submitted documents. Disabled until there is a query, so an empty search bar
 * leaves the full submitted list in charge. */
export function useDocumentSearch(roomId: string, params: SearchDocumentsQuery) {
  return useQuery({
    queryKey: documentSearchQueryKey(roomId, params),
    queryFn: () => fetchDocumentSearch(roomId, params),
    enabled: hasSearchCriteria(params),
    retry: false,
  });
}

/** The room's category hierarchy (PRD-0004 §2.2) — the folder tree the seller
 * browses their submitted documents through, read-only. `retry: false` so a 401
 * surfaces immediately (the page redirects to /login on it). */
export function useRoomCategories(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'categories'],
    queryFn: () => fetchRoomCategories(roomId),
    select: (r: ListCategoriesResponse) => r.categories,
    retry: false,
  });
}

/** The same read's checklist phases and order (display only — the folder tree
 * reads Phase › Category › … in the checklist's order, lib/folder-phases). One
 * request serves both hooks: the cache holds the whole response. */
export function useRoomFolderOrder(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'categories'],
    queryFn: () => fetchRoomCategories(roomId),
    select: (r: ListCategoriesResponse) => ({
      phases: r.phases ?? [],
      categoryOrder: r.categoryOrder ?? [],
    }),
    retry: false,
  });
}

function fetchRoomCategories(roomId: string): Promise<ListCategoriesResponse> {
  return api.get<ListCategoriesResponse>(`/rooms/${roomId}/categories`);
}

/** Finalize a single seller upload (base64 payload → 201). On success the
 * caller invalidates the documents query so the new row appears. */
export function useFinalizeIngest(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IngestFinalizeRequest) =>
      api.post<IngestFinalizeResponse>(`/rooms/${roomId}/ingest/finalize`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] });
      // An active search must show the new upload too.
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'search'] });
      // The upload may be matched to requested items: refresh the checklist.
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-summary'] });
    },
  });
}

/**
 * A READABLE COPY of a file the server could not read (ADR-0018 update
 * 2026-10-09): `POST /rooms/:roomId/documents/:documentId/readable-copy`, the
 * finalize body, stored as a new version of that document. 409 when the copy is
 * the same unreadable file again (or the document was read fine).
 */
export function useUploadReadableCopy(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: IngestFinalizeRequest }) =>
      api.post<IngestFinalizeResponse>(
        `/rooms/${roomId}/documents/${documentId}/readable-copy`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'search'] });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-summary'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Seller LOI response surface ("separate seller login" slice 4). The seller
// portal shows the room's LOIs and — when the room's seller_can_respond_loi
// toggle is on — records the same responses the intermediary has in web/admin
// (Accept / Counter / Reject, ADR-0029). The decision route allows the bound
// seller; GET /:roomId/lois is admin-OR-seller. Mirrors web/admin/src/api/hooks.ts.
// ---------------------------------------------------------------------------

/** The room's read model, from the seller-safe single-room read
 * `GET /rooms/:roomId` (admin-OR-seller gated server-side). The seller portal
 * reads only `sellerCanRespondLoi` from it, to decide whether to offer the LOI
 * responses or show them read-only. The tenant room LIST (`GET /rooms`) is
 * tenant-admin-only (room names leak deal information), so the bound seller —
 * who is not a tenant admin — reads exactly their OWN room here instead. A
 * seller not bound to the room gets a 404, which surfaces as a query error
 * (correct: they have no business reading it). `retry: false` so that surfaces
 * immediately. */
export function useSellerRoom(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId],
    queryFn: () => api.get<Room>(`/rooms/${roomId}`),
    retry: false,
  });
}

/** The room's LOIs as a SELLER sees them: the submitting buyer's funnel position,
 * exclusivity, the final `outcome` and the open `sellerVote`. Not gated by the
 * respond-toggle. Mount only for a seller: anyone else gets a 404 (`enabled`
 * false reads nothing — the dashboard asks only for an owner). */
export function useRoomLois(roomId: string, enabled = true) {
  return useQuery({
    queryKey: ['rooms', roomId, 'lois'],
    enabled,
    queryFn: () => api.get<ListLoisResponse>(`/rooms/${roomId}/lois`).then((r) => r.lois),
    retry: false,
  });
}

/** The same route as a seller's-team member with the LEGAL role is served it: the
 * document and the final outcome only, no votes. Mount only for such a member
 * (see `isLegalTeamOnly`); the two hooks share a cache key because one person is
 * only ever one of the two in a room. */
export function useRoomLoiOutcomes(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'lois'],
    queryFn: () => api.get<ListLoiOutcomesResponse>(`/rooms/${roomId}/lois`).then((r) => r.lois),
    retry: false,
  });
}

/** Record Accept / Counter / Reject on a submitted LOI (ADR-0029). With several
 * sellers a response is that seller's VOTE: the response then carries the
 * `sellerVote` tally and the LOI stays `submitted` until sellers holding more
 * than half of the ownership approve. An accept carries its exclusivity terms
 * (ADR-0046). A 409 carries an open vote, an already-decided LOI, ownership that
 * does not total 100% or an exclusivity conflict; a 404 an unpermitted actor.
 * Invalidates the LOI listing and the exclusivity view so the tally refreshes. */
export function useDecideLoi(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loiId, body }: { loiId: string; body: LoiDecisionRequest }) =>
      api.post<LoiDecisionResponse>(`/rooms/${roomId}/loi/${loiId}/decision`, body),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

/** A seller's Approve / Decline on a counter or decline another seller proposed
 * (a `loi_response` vote). Changeable until the decision completes. */
export function useLoiSellerVote(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loiId, vote }: { loiId: string } & LoiSellerVoteRequest) =>
      api.post<LoiDecisionResponse>(`/rooms/${roomId}/loi/${loiId}/seller-vote`, { vote }),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

// ---------------------------------------------------------------------------
// Enforced exclusivity (ADR-0046). The seller VALIDATES (or declines) the
// intermediary's acceptance and extensions, and may extend (while they may
// respond to LOIs). Mirrors web/admin's hooks; the buyer apps have none of these.
// ---------------------------------------------------------------------------

/** The room's live exclusivity as the seller sees it. Polled every minute so
 * the "needs your validation" item appears without a reload. */
export function useRoomExclusivity(roomId: string | undefined) {
  return useQuery({
    queryKey: ['rooms', roomId, 'exclusivity'],
    queryFn: () => api.get<ExclusivityView>(`/rooms/${roomId}/exclusivity`),
    enabled: !!roomId,
    retry: false,
    refetchInterval: 60_000,
  });
}

function invalidateExclusivity(qc: ReturnType<typeof useQueryClient>, roomId: string) {
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'exclusivity'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'lois'] });
}

/** Validate / decline the intermediary's pending acceptance. */
export function useValidateExclusivity(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExclusivityValidationRequest) =>
      api.post<ExclusivityValidationResponse>(`/rooms/${roomId}/exclusivity/validation`, body),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

/** Validate / decline the intermediary's pending extension. */
export function useValidateExtension(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExclusivityValidationRequest) =>
      api.post<Exclusivity>(`/rooms/${roomId}/exclusivity/extension/validation`, body),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

/** Extend the in-effect exclusivity — the seller's own applies at once. */
export function useExtendExclusivity(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExtendExclusivityRequest) =>
      api.post<Exclusivity>(`/rooms/${roomId}/exclusivity/extension`, body),
    onSuccess: () => invalidateExclusivity(qc, roomId),
  });
}

// ---------------------------------------------------------------------------
// Seller-facing requested-documents CHECKLIST ("requested documents checklist"
// slice 5). The seller sees the intermediary's requested items grouped by phase
// (release-gated server-side, so only RELEASED phases reach them), each marked
// Provided / Still due, and self-services fulfillment by linking one of their
// already-submitted documents. The two read routes are paged + admin-OR-seller;
// the two `/documents` link/unlink routes are allowed for the bound seller
// (migration 0021). Mirrors the react-query shape used across this app.
// ---------------------------------------------------------------------------

/** The room's requested-documents checklist, PAGED (GET /:roomId/dd-requests).
 * Returns the FULL response (the page `requests` plus `total`, so the caller can
 * drive "load more" from `offset`/`limit`). Optional `phaseId`/`status` filters
 * scope the read; a seller only ever sees released-phase requests. `retry: false`
 * so a 401 surfaces immediately (the page redirects to /login on it). The params
 * ride in the query key so each phase/offset slice caches independently. */
export function useDdChecklist(roomId: string, params: ListDdRequestsQuery = {}) {
  return useQuery(ddChecklistQuery(roomId, params));
}

/** One slice of the checklist as a query definition — shared by {@link useDdChecklist}
 * and the dashboard's "Due soon", which reads several slices at once (same cache keys). */
function ddChecklistQuery(roomId: string, params: ListDdRequestsQuery) {
  return {
    queryKey: ['rooms', roomId, 'dd-requests', params],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params.phaseId) qs.set('phaseId', params.phaseId);
      if (params.status) qs.set('status', params.status);
      if (params.limit != null) qs.set('limit', String(params.limit));
      if (params.offset != null) qs.set('offset', String(params.offset));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return api.get<ListDdRequestsResponse>(`/rooms/${roomId}/dd-requests${suffix}`);
    },
    retry: false as const,
  };
}

/** The seller's WHOLE checklist in one query (checklist redesign, seller page):
 * the same paged list endpoint, read at its largest page size — page 1 says
 * `total`, then the remaining pages in parallel. The server release-gates it, so
 * it holds released phases' items only. Counts per category, search and filters
 * are then exact, client-side, and certifying always sees every item of a
 * section. Not refetched on window focus. Fresh for the app's default staleTime
 * (App.tsx), the same as {@link useDdSummary}, so the list and the counts tiles
 * above it go stale together and an AI match or an admin release shows in both
 * at once. The seller's own single-item writes (link, unlink, "doesn't apply")
 * patch it in place; uploads and roster changes invalidate the `dd-requests`
 * prefix, which refetches it. `retry: false` so a 401 surfaces at once. */
export function useAllDdChecklist(roomId: string) {
  return useQuery({
    queryKey: allDdRequestsKey(roomId),
    queryFn: () => fetchAllDdRequests(roomId),
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Items per page when the dashboard reads a whole phase (the list endpoint's max). */
const DASHBOARD_PAGE_SIZE = 200;

/**
 * Every item of the given phases, read in pages of {@link DASHBOARD_PAGE_SIZE}: the
 * dashboard finds the items due soon among them (the API has no due-date filter).
 * The first page of a phase is the same cache entry the home checklist uses.
 */
export function useDdRequestsOfPhases(roomId: string, phases: readonly DdPhaseSummary[]) {
  const queries = phases.flatMap((phase) =>
    Array.from({ length: Math.max(1, Math.ceil(phase.total / DASHBOARD_PAGE_SIZE)) }, (_, i) =>
      ddChecklistQuery(roomId, {
        phaseId: phase.phaseId ?? undefined,
        limit: DASHBOARD_PAGE_SIZE,
        offset: i * DASHBOARD_PAGE_SIZE,
      }),
    ),
  );
  return useQueries({
    queries,
    combine: (results) => ({
      isPending: results.some((r) => r.isPending),
      error: results.find((r) => r.error)?.error ?? null,
      requests: results.flatMap((r) => r.data?.requests ?? []),
    }),
  });
}

/** The per-phase progress rollup (GET /:roomId/dd-requests/summary) — the
 * PRIMARY progress surface: one entry per (released, for a seller) phase with
 * `total`/`provided`/`due` + `released`. `retry: false` so a 401 surfaces
 * immediately (the page redirects to /login on it). */
export function useDdSummary(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-summary'],
    queryFn: () =>
      api
        .get<DdRequestsSummaryResponse>(`/rooms/${roomId}/dd-requests/summary`)
        .then((r) => r.phases),
    retry: false,
  });
}

/** Fulfill a requested item by LINKING one of the seller's submitted documents
 * (POST /:roomId/dd-requests/:requestId/documents). Flips the item's derived
 * `provided`. The item is patched into the whole checklist; the other checklist
 * slices, the summary rollup and the certifications refetch. The API also
 * settles the AI's suggestions of that document on EVERY item, so if another
 * cached item was waiting on it every checklist read refetches instead. */
export function useLinkDdDocument(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, documentId }: { requestId: string; documentId: string }) =>
      api.post<DdRequest>(`/rooms/${roomId}/dd-requests/${requestId}/documents`, { documentId }),
    onSuccess: (updated, { documentId }) => {
      if (otherItemsWaitingOn(qc, roomId, updated.id, documentId)) {
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] });
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-summary'] });
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] });
      } else {
        patchDdRequestInCache(qc, roomId, updated);
      }
    },
  });
}

/** Remove a linked document from a requested item (DELETE
 * /:roomId/dd-requests/:requestId/documents/:documentId). `provided` falls back
 * to false once the last link is gone (unless an admin completed-override holds).
 * Patches the item into the whole checklist; the other checklist slices, the
 * summary and the certifications (an unlink can re-open a section) refetch. */
export function useUnlinkDdDocument(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, documentId }: { requestId: string; documentId: string }) =>
      api.delete<DdRequest>(`/rooms/${roomId}/dd-requests/${requestId}/documents/${documentId}`),
    onSuccess: (updated) => patchDdRequestInCache(qc, roomId, updated),
  });
}

/** Set/clear the seller's ATTESTED "doesn't apply to my company" on a requested
 * item (PATCH /:roomId/dd-requests/:requestId/na, migrations 0022 + 0051).
 * Marking needs `attest: true` — the seller confirmed NA_ATTESTATION_TEXT in the
 * attestation dialog — and RESOLVES the item without a document; clearing returns
 * it to missing. Patches the item into the whole checklist; the other checklist
 * slices, the summary rollup and the certifications (a change re-opens a
 * certified section) refetch. */
export function useSetDdNotApplicable(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      notApplicable,
      attest,
    }: {
      requestId: string;
      notApplicable: boolean;
      attest?: boolean;
    }) =>
      api.patch<DdRequest>(
        `/rooms/${roomId}/dd-requests/${requestId}/na`,
        attest ? { notApplicable, attest: true } : { notApplicable },
      ),
    onSuccess: (updated) => patchDdRequestInCache(qc, roomId, updated),
  });
}

// ---------------------------------------------------------------------------
// SECTION CERTIFICATIONS (migration 0038) — the seller "Certified" tick (🟢).
// The bound SELLER attests a DD section ('complete' / 'not_applicable') or
// withdraws it; every attestation is hash-chained server-side. The read is
// admin-OR-seller; the two writes are SELLER-ONLY. `sectionKey` is the opaque
// per-section key the UI forms (lib/section.ts) and is URL-encoded into the path.
// ---------------------------------------------------------------------------

/** The room's section certifications (GET /:roomId/certifications), keyed on the
 * opaque `sectionKey`. `retry: false` so a 401 surfaces immediately. */
export function useRoomCertifications(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'certifications'],
    queryFn: () =>
      api
        .get<ListCertificationsResponse>(`/rooms/${roomId}/certifications`)
        // One list keyed by section: current attestations plus the ones a later
        // change invalidated (flagged, so the UI shows "Needs re-certification").
        .then((r) => [
          ...r.certifications,
          ...(r.needsRecertification ?? []).map((c) => ({ ...c, needsRecertification: true })),
        ]),
    retry: false,
  });
}

/** CERTIFY a section (POST /:roomId/sections/:sectionKey/certify, seller-only).
 * Upserts the current attestation; invalidates the certifications query so the
 * green state refreshes. A 404 means the caller is not the bound seller. */
export function useCertifySection(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionKey, body }: { sectionKey: string; body: CertifySectionRequest }) =>
      api.post<SectionCertification>(
        `/rooms/${roomId}/sections/${encodeURIComponent(sectionKey)}/certify`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] }),
  });
}

/** WITHDRAW a section's certification (DELETE /:roomId/sections/:sectionKey/certify,
 * seller-only). A 404 means there was nothing to withdraw (or the caller is not
 * the bound seller). Invalidates the certifications query on success. */
export function useWithdrawCertification(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionKey: string) =>
      api.delete<WithdrawCertificationResponse>(
        `/rooms/${roomId}/sections/${encodeURIComponent(sectionKey)}/certify`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] }),
  });
}

// ---------------------------------------------------------------------------
// PHASE QUESTIONS (migration 0024) — the seller-facing "Phase Questions" form.
// The seller sees the intermediary's free-text questions for each RELEASED phase
// (server release-gates the read) and answers them. The answer route is
// admin-OR-seller; the seller answers their own room's questions.
// ---------------------------------------------------------------------------

/** The questions the intermediary defined under one released phase
 * (GET /:roomId/dd-questions?phaseId=), each with the seller's current answer.
 * A seller only ever sees released-phase questions (server-enforced).
 * `retry: false` so a 401 surfaces immediately (the page redirects to /login). */
export function useDdQuestions(roomId: string, phaseId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-questions', phaseId],
    queryFn: () =>
      api
        .get<ListDdPhaseQuestionsResponse>(`/rooms/${roomId}/dd-questions?phaseId=${phaseId}`)
        .then((r) => r.questions),
    retry: false,
  });
}

/** Answer (or clear) a phase question (PATCH /:roomId/dd-questions/:questionId/
 * answer). An empty `answer` clears it back to unanswered. Invalidates the loaded
 * phase-questions lists so the saved answer shows immediately. */
export function useAnswerPhaseQuestion(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      questionId,
      body,
    }: {
      questionId: string;
      body: AnswerDdPhaseQuestionRequest;
    }) => api.patch<DdPhaseQuestion>(`/rooms/${roomId}/dd-questions/${questionId}/answer`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-questions'] }),
  });
}

/** Buyer questions the intermediary sent to this seller (ADR-0039). The API
 * returns only routed questions, with no buyer identity. */
export function useSellerQuestions(roomId: string) {
  return useQuery({
    queryKey: questionsQueryKey(roomId),
    // The whole response: `mode` says whether the buyer runs this room (answers go straight to them).
    queryFn: () => listQuestions(roomId),
    retry: false,
  });
}

/** The room's shared Buyer Questions (ADR-0039), read-only for the seller. */
export function useBuyerQuestions(roomId: string) {
  return useQuery({
    queryKey: buyerQuestionsQueryKey(roomId),
    queryFn: () => listBuyerQuestions(roomId),
    retry: false,
  });
}

/** Draft (or redraft) an answer; it waits for the intermediary to release it — or,
 * in a buyer-run room, goes straight to the buyer. */
export function useDraftAnswer(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, answer }: { questionId: string; answer: AnswerQuestionRequest }) =>
      answerQuestion(roomId, questionId, answer),
    onSuccess: () => qc.invalidateQueries({ queryKey: questionsQueryKey(roomId) }),
  });
}

// ---------------------------------------------------------------------------
// DD ACCOUNT ROSTER (ADR-0037) — the seller lists the company's accounts (the
// answers to the Financial roster questions); each becomes its own checklist
// category. The seller sees only the kinds in a released phase (server-gated)
// and can add and correct accounts, never merge. A write creates/relabels
// categories and items, so the checklist reads refresh too. The full number is
// fetched by `revealDdAccountNumber` (@dataroom/shared) on an explicit click only.
// ---------------------------------------------------------------------------

function invalidateDdAccounts(qc: ReturnType<typeof useQueryClient>, roomId: string): void {
  qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-accounts'] });
  qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] });
  qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-summary'] });
  qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
  qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] });
}

/** The account kinds released to the seller and their roster (GET /:roomId/dd-accounts). */
export function useDdAccounts(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-accounts'],
    queryFn: () => api.get<ListDdAccountsResponse>(`/rooms/${roomId}/dd-accounts`),
    retry: false,
  });
}

/** Add an account (POST /:roomId/dd-accounts); `created: false` when it was already listed. */
export function useCreateDdAccount(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDdAccountRequest) =>
      api.post<CreateDdAccountResponse>(`/rooms/${roomId}/dd-accounts`, body),
    onSuccess: () => invalidateDdAccounts(qc, roomId),
  });
}

/** Rename / correct an account (PATCH /:roomId/dd-accounts/:accountId). */
export function useUpdateDdAccount(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }: { accountId: string; body: UpdateDdAccountRequest }) =>
      api.patch<DdAccount>(`/rooms/${roomId}/dd-accounts/${accountId}`, body),
    onSuccess: () => invalidateDdAccounts(qc, roomId),
  });
}

// ---------------------------------------------------------------------------
// DD STATE ROSTER (issue #157) — the seller chooses the states the company
// operates in (once the phase holding the "[State]" items is released; the
// server gates it). Each chosen state gets its own checklist categories, so a
// write refreshes the checklist reads too.
// ---------------------------------------------------------------------------

/** The jurisdictions and the room's chosen states (GET /:roomId/dd-states). */
export function useDdStates(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'dd-states'],
    queryFn: () => api.get<ListDdStatesResponse>(`/rooms/${roomId}/dd-states`),
    retry: false,
  });
}

/** Set the chosen states (PUT /:roomId/dd-states) — the full set of codes. */
export function useSetDdStates(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (states: string[]) =>
      api.put<SetDdStatesResponse>(`/rooms/${roomId}/dd-states`, { states }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-states'] });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-summary'] });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'categories'] });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] });
    },
  });
}

/** The risks & issues the deal team shared with the seller (ADR-0047) — the
 * seller's read-only projection of GET /:roomId/risks (no owner ids, no
 * visibility). Entries are written by the deal team, never by the AI. */
export function useSellerRisks(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'risks'],
    queryFn: () => api.get<ListRisksResponse>(`/rooms/${roomId}/risks`).then((r) => r.risks),
    retry: false,
  });
}

/** The timeline milestones shared with the seller (GET /:roomId/milestones), by date. */
export function useSellerMilestones(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'milestones'],
    queryFn: () =>
      api.get<ListMilestonesResponse>(`/rooms/${roomId}/milestones`).then((r) => r.milestones),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Contract expiration register (ADR-0054): the seller's own contracts with the
// dates each document states, corrections (audited) and "Upload a current copy".
// ---------------------------------------------------------------------------

/** GET /:roomId/contracts — the register over the seller's library. */
export function useContractRegister(roomId: string) {
  return useQuery({
    queryKey: contractsQueryKey(roomId),
    queryFn: () => fetchContractRegister(roomId),
    retry: false,
  });
}

/** PATCH /:roomId/contracts/:documentId — the seller corrects a date; the register refreshes. */
export function useCorrectContract(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: CorrectContractRequest }) =>
      correctContract(roomId, documentId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsQueryKey(roomId) }),
  });
}

/**
 * POST /:roomId/contracts/:documentId/current-copy — the current copy of an
 * expired contract, stored as a new version of it (409: the same file again).
 */
export function useUploadCurrentCopy(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, body }: { documentId: string; body: IngestFinalizeRequest }) =>
      uploadContractCurrentCopy(roomId, documentId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractsQueryKey(roomId) });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] });
      qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-requests'] });
    },
  });
}

/** The room's processing time estimate and per-stage tally (owner ask 2026-10-23;
 * GET /rooms/:id/processing/estimate): refreshes every ~10 s while work remains.
 * `enabled: false` reads nothing (the end-of-upload line asks only once a batch is done). */
export function useProcessingEstimate(roomId: string, options: { enabled?: boolean } = {}) {
  return useQuery<ProcessingEstimate>({
    queryKey: estimateQueryKey(roomId),
    queryFn: () => fetchProcessingEstimate(roomId),
    enabled: options.enabled ?? true,
    retry: false,
    refetchInterval: (query) =>
      estimateRefetchInterval(query.state.data, query.state.status === 'error'),
  });
}

/** What the processing page's poll remembers (a page of rows + the in-flight counts). */
const processingPoll = documentsPollTracker();

/** The room's PROCESSING STATUS page for the bound seller (GET /rooms/:id/processing):
 * the same view and cadence as the admin app's. */
export function useRoomProcessing(roomId: string, params: ProcessingParams, timeZone: string) {
  const days = uploadRangeDays(params, timeZone);
  return useQuery<ProcessingViewResponse>({
    queryKey: processingQueryKey(roomId, params, days),
    queryFn: () => fetchProcessingView(roomId, params, days),
    retry: false,
    placeholderData: (prev) => prev,
    refetchInterval: (query) =>
      nextDocumentsPoll(
        processingPollDocs(query.state.data),
        processingPoll,
        Date.now(),
        query.state.status === 'error',
      ),
  });
}
